import './CandidateModal.css';
import RenderAvatar from './RenderAvatar'
import LinkButton from './LinkButton';
import ConnectionButtons from './ConnectionButtons';

const CandidateModal = ({ candidate, onClose }) => {
  
  // const formatField = (text) => {
  //   if (!text) return text;
  //   return text.replace(/_/g, ' ');
  // };

  const formatResponsibility = (responsibility) => {
    const responsibilityMap = {
      'product': 'Product',
      'engineering': 'Engineering',
      'design': 'Design',
      'sales_marketing': 'Sales and marketing',
      'operations': 'Operations'
    };
    return responsibilityMap[responsibility] || responsibility;
  };

  const formatInterest = (interest) => {
    const interestMap = {
      'agriculture': 'Agriculture / Agtech',
      'ai': 'Artificial Intelligence',
      'arvr': 'Augmented Reality / Virtual Reality',
      'b2b': 'B2B / Enterprise',
      'biotech': 'Biomedical / Biotech',
      'blockchain': 'Blockchain',
      'climate': 'Climate / Sustainability',
      'consumer': 'Consumer',
      'ecommerce': 'E-Commerce',
      'devtools': 'Developer Tools',
      'edtech': 'Education / Edtech',
      'energy': 'Energy',
      'entertainment': 'Entertainment',
      'fintech': 'Financial / Fintech',
      'food': 'Food / Beverage',
      'gaming': 'Gaming',
      'government': 'Government',
      'hardware': 'Hardware',
      'hard_tech': 'Hard Tech',
      'health': 'Health / Wellness',
      'healthcare': 'Healthcare',
      'marketplace': 'Marketplace',
      'nonprofit': 'Non-Profit',
      'proptech': 'Real Estate / Proptech',
      'robotics': 'Robotics',
      'security': 'Security',
      'travel': 'Travel / Tourism'
    };
    return interestMap[interest] || interest;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    // Handle different date formats that might come from PostgreSQL vs Supabase
    let date;
    
    // If it's already a Date object
    if (dateString instanceof Date) {
      date = dateString;
    } else {
      // Try to parse the string
      // PostgreSQL might return timestamps like "2024-01-15 10:30:00" or "2024-01-15T10:30:00.000Z"
      // Supabase typically returns ISO strings
      date = new Date(dateString);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date received:', dateString);
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString();
  };

  const formatTextWithLineList = (text) => {
    if (!text) return null;
    
    // First replace URLs with <a> tags (exclude trailing punctuation except /)
    const urlRegex = /(https?:\/\/[^\s),!?;]+|www\.[^\s),!?;]+)/g;
    let formattedText = text.replace(urlRegex, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url;
      return `<a class="link" href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    
    // Split on newlines and filter empty lines
    const lines = formattedText.split('\n').filter(line => line.trim() !== '');
    
    return (
      <ul className="formatted-list">
        {lines.map((line, index) => (
          <li key={index}>
            <div 
              className="list-item-content"
              dangerouslySetInnerHTML={{ __html: line.trim() }}
            />
          </li>
        ))}
      </ul>
    );
  };

  const formatTextWithLineBreaks = (text) => {
    if (!text) return null;
    
    // First replace URLs with <a> tags (exclude trailing punctuation except /)
    const urlRegex = /(https?:\/\/[^\s),!?;]+|www\.[^\s),!?;]+)/g;
    let formattedText = text.replace(urlRegex, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url;
      return `<a class="link" href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    
    // Then replace \n with <br>
    formattedText = formattedText.replace(/\n/g, '<br>');
    
    return (
      <div 
        className="formatted-text-content"
        dangerouslySetInnerHTML={{ __html: formattedText }}
      />
    );
  };

  const getLinkedInUrl = (linkedin) => {
    if (!linkedin) return null;
    if (linkedin.startsWith('http')) return linkedin;
    return `https://linkedin.com/in/${linkedin}`;
  };

  const getCofounderMatchUrl = (slug) => {
    return `https://www.startupschool.org/cofounder-matching/candidate/${slug}`;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-header-container">
            <h1 className="modal-header-title">{candidate.name}</h1>
            <div className='modal-action-container'>
              <div className="modal-actions">
                <LinkButton url={getLinkedInUrl(candidate.linkedin)} name={'LinkedIn'} className='linkedin-btn' />
                <LinkButton url={getCofounderMatchUrl(candidate.slug)} name={'YC'} className='profile-btn' />
              </div>
              <ConnectionButtons candidateSlug={candidate.slug} />
            </div>
          </div>

          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="candidate-header-info">
            <div className="candidate-avatar-large">
              <RenderAvatar candidate={candidate} />
            </div>
            <div className="candidate-basic-info">
              <h2>{candidate.name}</h2>
              <div className="candidate-meta">
                <span className="age">{candidate.age ? `${candidate.age} years old` : 'Age not specified'}</span>
                {candidate.isWoman !== null && (
                  <span className={`gender ${candidate.isWoman ? 'woman' : 'man'}`}>{candidate.isWoman ? 'Woman' : 'Man'}</span>
                )}
                {candidate.isTechnical !== null && (
                  <span className={`technical ${candidate.isTechnical ? 'is-technical' : 'non-technical'}`}>
                    {candidate.isTechnical ? 'Technical' : 'Non-technical'}
                  </span>
                )}
                {candidate.ycFounderLabel && (
                  <span className="yc-founder">{candidate.ycFounderLabel}</span>
                )}
              </div>
              <div className="modal-location-info">
                {candidate.location && <span className="location">{candidate.location},</span>}
                {candidate.region && <span className="location">{candidate.region}</span>}
              </div>
              <div className=''>
                {candidate.timing && (
                  <div className="detail-item">
                    <strong>Timing:</strong> {candidate.timing}
                  </div>
                )}
                {candidate.hasIdea && (
                  <div className="detail-item">
                    <strong>Business Idea: </strong> {candidate.hasIdea}
                  </div>
                )}
              </div>
              {candidate.videoLink && (
                <div className="detail-item">
                  <strong>Video:</strong>
                  <a href={candidate.videoLink} target="_blank" rel="noopener noreferrer">
                    Watch Video
                  </a>
                </div>
              )}
              {candidate.calendlyLink && (
                <div className="detail-item">
                  <strong>Schedule Meeting:</strong>
                  <a href={candidate.calendlyLink} target="_blank" rel="noopener noreferrer">
                    Book on Calendly
                  </a>
                </div>
              )}
            </div>
          </div>
          {candidate.intro && (
            <div className="section intro-section">
              <h3>Introduction</h3>
              {formatTextWithLineBreaks(candidate.intro)}
            </div>
          )}

          {candidate.lifeStory && (
            <div className="section life-story-section">
              <h3>Life Story</h3>
              <div className="life-story-content">
                {formatTextWithLineBreaks(candidate.lifeStory)}
              </div>
            </div>
          )}

          {candidate.freeTime && (
            <div className="section free-time-section">
              <h3>Free Time</h3>
              <div className="free-time-content">
                {formatTextWithLineBreaks(candidate.freeTime)}
              </div>
            </div>
          )}

          {candidate.other && (
            <div className="section other-section">
              <h3>Other</h3>
              <div className="other-content">
                {formatTextWithLineBreaks(candidate.other)}
              </div>
            </div>
          )}

          <div className="section details-section">
            <div className="professional-info">
              {candidate.impressiveThing && (
                <div className="impressive-achievement-box">
                  <h4>Impressive Accomplishment</h4>
                  <div className="impressive-content">
                    {formatTextWithLineBreaks(candidate.impressiveThing)}
                  </div>
                </div>
              )}

              {candidate.education && (
                <div className="education-box">
                  <h4>Education</h4>
                  {formatTextWithLineList(candidate.education)}
                </div>
              )}

              {candidate.employment && (
                <div className="employment-box">
                  <h4>Employment</h4>
                  {formatTextWithLineList(candidate.employment)}
                </div>
              )}

              {candidate.companyName && (
                <div className="company-info-box">
                  <h4>Company</h4>

                  <div className="company-name">
                    <strong>Name: </strong>
                    <strong>{candidate.companyName}</strong>
                  </div>
                  {candidate.companyUrl && (
                    <div className="company-url">
                      <strong>URL: </strong>
                      <a className='link' href={candidate.companyUrl} target="_blank" rel="noopener noreferrer">
                        {candidate.companyUrl}
                      </a>
                    </div>
                  )}
                  {candidate.companyDescription && (
                    <div className="company-description">
                      <strong>Company Description: </strong>
                      {formatTextWithLineBreaks(candidate.companyDescription)}
                    </div>
                  )}
                  {candidate.companyProgress && (
                    <div className="company-progress">
                      <strong>Company Progress: </strong>
                      {formatTextWithLineBreaks(candidate.companyProgress)}
                    </div>
                  )}
                  {candidate.fundingStatus && (
                    <div className="funding-status">
                      <strong>Funding Status: </strong>
                      {formatTextWithLineBreaks(candidate.fundingStatus)}
                    </div>
                  )}
                </div>
              )}

              {candidate.ideas && (
                <div className="ideas-info-box">
                  <h4>Potential Ideas</h4>
                  {formatTextWithLineBreaks(candidate.ideas)}
                </div>
              )}
              
              {(candidate.hasCf) && (
                <div className="cofounder-info-box">
                  <h4>Current Co-founder Information</h4>
                  {candidate.hasCf !== null && (
                    <div className="cf-status">
                      <p>I already have a <strong>{candidate.currentCfTechnical ? 'technical': 'non-technical'}</strong> co-founder.</p>&nbsp;
                      {candidate.currentCfLinkedin && (
                          <a className='link' href={getLinkedInUrl(candidate.currentCfLinkedin)} target="_blank" rel="noopener noreferrer">
                            View Linkedin Profile
                          </a>
                      )}
                    </div>
                  )}
                  {candidate.whyLookingForThirdCf && (
                    <div className="another-cofounder-requirement">
                      <strong>Why I'm looking for another co-founder</strong>
                      <p>{candidate.whyLookingForThirdCf}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          {(candidate.interests && candidate.interests.length > 0) || 
           (candidate.responsibilities && candidate.responsibilities.length > 0) ? (
            <div className="section candidate-profile-section">
              <h3>My Preferences</h3>
              <div className="profile-content">
                {candidate.interests && candidate.interests.length > 0 && (
                  <div className="profile-item">
                    <h4>Interests</h4>
                    <div className="tags">
                      {candidate.interests.map((interest, index) => (
                        <span key={index} className="tag interest-tag">{formatInterest(interest)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {candidate.responsibilities && candidate.responsibilities.length > 0 && (
                  <div className="profile-item">
                    <h4>Preferred Responsibilities</h4>
                    <div className="tags">
                      {candidate.responsibilities.map((responsibility, index) => (
                        <span key={index} className="tag responsibility-tag">{formatResponsibility(responsibility)}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <div className="section cofounder-preferences">
            <h3>Co-founder Preferences</h3>
            <div className="cf-details">
              {candidate.cfHasIdea !== null && (
                <div className="cf-detail">
                  <strong>Wants Co-founder with Idea:</strong> {candidate.cfHasIdea ? 'Yes' : 'No'}
                  {candidate.cfHasIdeaImportance && (
                    <span className="importance"> (Importance: {candidate.cfHasIdeaImportance})</span>
                  )}
                </div>
              )}
              {candidate.cfIsTechnical !== null && (
                <div className="cf-detail">
                  <strong>Seeking Technical Co-founder:</strong> {candidate.cfIsTechnical ? 'Yes' : 'No'}
                  {candidate.cfIsTechnicalImportance && (
                    <span className="importance"> (Importance: {candidate.cfIsTechnicalImportance})</span>
                  )}
                </div>
              )}
              {candidate.cfLocation && (
                <div className="cf-detail">
                  <strong>Preferred Location:</strong>
                  {candidate.cfLocation === 'geographic' ? (
                    <span> Within {candidate.cfLocationKmRange} km</span>
                  ) : (
                    <span> {candidate.cfLocation}</span>
                  )}
                  {candidate.cfLocationImportance && (
                    <span className="importance"> (Importance: {candidate.cfLocationImportance})</span>
                  )}
                </div>
              )}
              {(candidate.cfAgeMin || candidate.cfAgeMax) && (
                <div className="cf-detail">
                  <strong>Preferred Age Range:</strong>
                  {candidate.cfAgeMin && candidate.cfAgeMax ? ` ${candidate.cfAgeMin}-${candidate.cfAgeMax}` :
                    candidate.cfAgeMin ? ` ${candidate.cfAgeMin}+` :
                      candidate.cfAgeMax ? ` Up to ${candidate.cfAgeMax}` : ''}
                  {candidate.cfAgeImportance && (
                    <span className="importance"> (Importance: {candidate.cfAgeImportance})</span>
                  )}
                </div>
              )}
              {candidate.cfTimingImportance && (
                <div className="cf-detail">
                  <strong>Timing Match Importance:</strong> {candidate.timing}
                  <span className="importance"> (Importance: {candidate.cfTimingImportance})</span>
                </div>
              )}
              {candidate.cfInterestsImportance && (
                <div className="cf-detail">
                  <strong>Interests Match Importance:</strong> {candidate.cfInterestsImportance}
                </div>
              )}
              {candidate.cfResponsibilities && candidate.cfResponsibilities.length > 0 && (
                <div className="cf-detail">
                  <strong>Preferred Co-founder Responsibilities:</strong>
                  {candidate.cfResponsibilitiesImportance && (
                    <span className="importance"> (Importance: {candidate.cfResponsibilitiesImportance})</span>
                  )}
                  <div className="tags">
                    {candidate.cfResponsibilities.map((responsibility, index) => (
                      <span key={index} className="tag cf-responsibility-tag">{formatResponsibility(responsibility)}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Additional Importance Preferences */}
              {candidate.cfIsFellowAlumniImportance && (
                <div className="cf-detail">
                  <strong>Wants Fellow Alumni Co-founder:</strong> {candidate.cfIsFellowAlumniImportance}
                </div>
              )}
              {candidate.cfIsYcAlumImportance && (
                <div className="cf-detail">
                  <strong>Wants YC Alumni Co-founder:</strong> {candidate.cfIsYcAlumImportance}
                </div>
              )}
              {candidate.cfIsWomanImportance && (
                <div className="cf-detail">
                  <strong>Wants Woman Co-founder:</strong> {candidate.cfIsWomanImportance}
                </div>
              )}
              
              {/* Additional Requirements and Equity */}
              {candidate.reqFreeText && (
                <div className="cf-detail">
                  <strong>Ideal co-founder:</strong>
                  <div className="requirements-text">{formatTextWithLineBreaks(candidate.reqFreeText)}</div>
                </div>
              )}
              {candidate.equity && (
                <div className="cf-detail">
                  <strong>Equity Expectations:</strong>
                  {formatTextWithLineBreaks(candidate.equity)}
                </div>
              )}
            </div>
          </div>

          {/* {candidate.emailSettings && candidate.emailSettings.length > 0 && (
            <div className="section email-settings-section">
              <h3>Email Settings</h3>
              <div className="email-settings-content">
                <div className="tags">
                  {candidate.emailSettings.map((setting, index) => (
                    <span key={index} className="tag email-setting-tag">{formatField(setting)}</span>
                  ))}
                </div>
              </div>
            </div>
          )} */}
        </div>

        <div className="modal-footer">
          <div className="timestamps">
            <div className="timestamp-item">
              <strong>Last Seen:</strong> {formatDate(candidate.lastSeenAt)}
            </div>
            <div className="timestamp-item">
              <strong>Last Updated:</strong> {formatDate(candidate.savedAt)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateModal;